import { Button } from '@sunwebgroup/uds-ui/button'

import { Dialog } from '@sunwebgroup/uds-ui/dialog'

import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'

import { Typography } from '@sunwebgroup/uds-ui/typography'

import { isEmpty } from 'ramda'
import * as React from 'react'
import { useState } from 'react'
import { toSnakeCase } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import {
  CarsPagination,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/CarsPagination'
import { CarView } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/CarView'
import {
  FilterableCarsSkeleton,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/FilterableCarsSkeleton'
import { AdditionalServicesFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/AdditionalServicesFilter'
import { CarClassFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/CarClassFilter'
import { ChildSeatsFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/ChildSeatsFilter'
import { DepositTypeFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/DepositTypeFilter'
import { DriverAgeFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/DriverAgeFilter'
import { FuelTypeFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/FuelTypeFilter'
import { PickUpLocationFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/PickUpLocationFilter'
import { TransmissionFilter } from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/TransmissionFilter'
import { selectedFiltersSelector } from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors'
import { useFilterableCarsAvailable } from '@/scopes/checkout/filterableCars/domain/hooks'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import styles from './FilterableCarsAvailable.module.css'

export function FilterableCarsAvailable() {
  const { isLoading } = useFilterableCarsAvailable()

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <IndeterminateProgressCircle size={32} />
      </div>
    )
  }

  return (
    <Sheet.Content className={styles.sheetContent}>
      <div className={styles.wrapper}>
        <FiltersSection />
        <CarsSection />
      </div>
    </Sheet.Content>
  )
}

function FiltersSection() {
  const { isDesktopSm } = useBreakpoints()
  const [showFilters, setShowFilters] = useState(true)
  const intl = useIntl()
  const { filters, totalCars } = useFilterableCarsAvailable()

  const selectedFilters = selectedFiltersSelector(filters)

  const filtersContent = (
    <div className={styles.filtersSectionWrapper}>
      <DriverAgeFilter />
      <PickUpLocationFilter />
      <AdditionalServicesFilter />
      <TransmissionFilter />
      <CarClassFilter />
      <FuelTypeFilter />
      <DepositTypeFilter />
      <ChildSeatsFilter />
    </div>
  )

  if (!isDesktopSm) {
    return (
      <>
        <ListItem variant="transparent" className={styles.showFiltersTriggerSection} onClick={() => setShowFilters(true)}>
          {selectedFilters && (
            <Typography variant="body-m-bold-tight" className={styles.selectedFiltersSummary}>
              {Object.entries(selectedFilters).length === 0 && intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_no_filters_selected_text' }) }
              {Object.entries(selectedFilters).length > 0 && Object.entries(selectedFilters)
                .flatMap(([key, values]) =>
                  values
                    .map(value =>
                      intl.formatMessage({
                        id: `checkout_steps_ancillaries_car_rental_filtering_filters_${toSnakeCase(key)}_type_${toSnakeCase(value)}_text`,
                        defaultMessage: value,
                      }),
                    )
                    .join(', '),
                )
                .join(', ')}
            </Typography>
          )}

          <Button variant="primary" size="S" className={styles.showFiltersTriggerSectionButton}>
            {intl.formatMessage({
              id: 'checkout_steps_ancillaries_car_rental_filtering_filters_show_all_cars_change_filters_button',
            })}
          </Button>
        </ListItem>
        <Sheet.Root open={showFilters} onOpenChange={setShowFilters}>
          <Sheet.Body side="bottom" hasPadding={false}>
            <Sheet.Header titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_all_cars_title' })} />
            <Sheet.Content>
              {filtersContent}
            </Sheet.Content>
            <Sheet.Footer>
              <div className={styles.filtersBottomSection}>
                <Button variant="primary" onClick={() => setShowFilters(false)} fullWidth>
                  {intl.formatMessage(
                    { id: 'checkout_steps_ancillaries_car_rental_filtering_filters_show_all_cars_button' },
                    { amount: totalCars },
                  )}
                </Button>
              </div>
            </Sheet.Footer>
          </Sheet.Body>
        </Sheet.Root>
      </>
    )
  }
  return filtersContent
}

function CarsSection() {
  const { isFetching, paginatedCars } = useFilterableCarsAvailable()

  if (isFetching) {
    return (
      <div className={styles.carsSectionWrapper}>
        <div className={styles.mainSection}>
          <FilterableCarsSkeleton />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.carsSectionWrapper}>
      <div className={styles.mainSection}>
        {(!isFetching && !isEmpty(paginatedCars)) ? (<PaginatedAvailableCars />) : ''}
        {(!isFetching && isEmpty(paginatedCars)) ? (<FilterableCarsEmptyState />) : ''}
      </div>
    </div>
  )
}

function PaginatedAvailableCars() {
  const intl = useIntl()
  const { paginatedCars } = useFilterableCarsAvailable()
  const carsElement = React.useRef<HTMLDivElement | null>(null)

  return (
    <div className={styles.cars} ref={carsElement}>
      <Typography variant="body-m-regular-tight" className={styles.sortedExplanation}>
        { intl.formatMessage({
          id: 'checkout_steps_ancillaries_car_rental_filtering_sort_cars_text',
        })}
      </Typography>

      {Object.values(paginatedCars ?? {}).map(car => (
        <CarView key={car.id} {...car} />
      ))}

      <CarsPagination scrollToElement={carsElement} />
    </div>
  )
}

function FilterableCarsEmptyState() {
  const intl = useIntl()

  return (
    <Dialog
      className={styles.emptyCars}
      type="custom"
      text={intl.formatMessage({
        id: 'checkout_steps_ancillaries_car_rental_filtering_cars_empty_state_title',
      })}
      supportingText={intl.formatMessage({
        id: 'checkout_steps_ancillaries_car_rental_filtering_cars_empty_state_subtitle',
      })}
      icon="FaceSad"
    />
  )
}
